const DEFAULT_SPORTS = [
  {
    id: 'corrida',
    name: 'Corrida 5 km',
    active: true,
    selection_type: 'individual',
    shirt: {
      enabled: true,
      required: true,
      sizes: ['P', 'M', 'G', 'GG', 'XG'],
    },
  },
  {
    id: 'tenis',
    name: 'Tênis de Campo',
    active: true,
    selection_type: 'individual',
    categories: {
      enabled: true,
      required: true,
      multiple: false,
      options: [
        'Tênis I – Intermediário / Avançado',
        'Tênis II – Iniciante / Intermediário',
        'Feminino',
      ],
    },
  },
  {
    id: 'domino',
    name: 'Dominó',
    active: true,
    selection_type: 'double',
    partner_name: {
      enabled: true,
      required: false,
      label: 'Nome do parceiro (opcional)',
    },
  },
  {
    id: 'beach_tennis',
    name: 'Beach Tennis',
    active: true,
    selection_type: 'double',
    categories: {
      enabled: true,
      required: true,
      multiple: true,
      options: ['Masculino', 'Feminino', 'Misto'],
    },
    partner_name: {
      enabled: true,
      required: false,
      label: 'Nome do parceiro(a) (opcional)',
    },
  },
  {
    id: 'futebol',
    name: 'Futebol',
    active: true,
    selection_type: 'individual',
  },
  {
    id: 'volei',
    name: 'Vôlei de Praia 4x4',
    active: true,
    selection_type: 'team',
  },
  {
    id: 'nao_participar_esportes',
    name: 'Não irei participar de modalidades esportivas',
    active: true,
    special_type: 'opt_out',
  },
];

export const DEFAULT_SPORT_MODALITY = {
  id: '',
  name: '',
  active: true,
  selection_type: 'individual',
  special_type: '',
  shirt: {
    enabled: false,
    required: false,
    sizes: '',
  },
  categories: {
    enabled: false,
    required: false,
    multiple: false,
    max_selection: 2,
    options: '',
  },
  partner_name: {
    enabled: false,
    required: false,
    label: 'Nome do parceiro(a)',
  },
};

export const DEFAULT_SARAU_MODALITY = {
  id: '',
  name: '',
  active: true,
  special_type: '',
  extra_text: {
    enabled: false,
    required: false,
    label: 'Descreva a atividade',
  },
};

const DEFAULT_SARAU = [
  { id: 'artes_visuais', name: 'Artes Visuais', active: true },
  { id: 'literatura', name: 'Literatura', active: true },
  { id: 'artes_cenicas', name: 'Artes Cênicas', active: true },
  { id: 'musica', name: 'Música', active: true },
  { id: 'danca', name: 'Dança', active: true },
  {
    id: 'outros',
    name: 'Outros',
    active: true,
    extra_text: {
      enabled: true,
      required: false,
      label: 'Descreva a atividade',
    },
  },
];

export const DEFAULT_HOTEL_ROOM_RULES = {
  room_people_limit: 3,
  minor_age_limit: 17,
  age_validation_mode: 'birth_year',
  first_room: {
    paying_companion_limit: 2,
    companion_price: 700,
    free_companion_limit: 0,
  },
  additional_room: {
    paying_companion_limit: 2,
    free_companion_limit: 0,
    charge_enabled: true,
    pricing_tiers: [{ room_price: 1000, companion_price: 0 }],
  },
  minors_free: true,
  minors_count_towards_room_limit: false,
  minors_count_towards_payment: false,
  total_rooms_available: 0,
  max_rooms_per_registration: 0,
};

export const DEFAULT_HOTEL = {
  id: '',
  name: '',
  description: '',
  active: true,
  room_rules: DEFAULT_HOTEL_ROOM_RULES,
};

export const DEFAULT_COPA_SARAU_CONFIG = {
  version: 2,
  sports_limit: 3,
  sarau_limit: 3,
  modalities: {
    sports: DEFAULT_SPORTS,
    sarau: DEFAULT_SARAU,
  },
  hotels: [
    {
      id: 'hotel_principal',
      name: 'Hotel principal',
      description: '',
      active: true,
      room_rules: { ...DEFAULT_HOTEL_ROOM_RULES },
    },
  ],
  room_rules: {
    ...DEFAULT_HOTEL_ROOM_RULES,
    pending_reservation_ttl_hours: 24,
  },
  pending_reservation_ttl_hours: 24,
  terms: {
    cancellation_phone: '',
  },
};

const normalizeNumber = (value, fallback) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const normalizeInteger = (value, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const normalizeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

const normalizeList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    return value.map((item) => `${item}`.trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return fallback;
};

const SCHEDULE_FIELDS = ['start', 'end', 'participationStart', 'participationEnd'];

const isMomentLike = (value) =>
  value &&
  typeof value === 'object' &&
  typeof value.toDate === 'function' &&
  typeof value.toISOString === 'function';

const toDateObject = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (isMomentLike(value)) {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const toIsoString = (value) => {
  const date = toDateObject(value);
  return date ? date.toISOString() : null;
};

const padDatePart = (value) => `${value}`.padStart(2, '0');

const toDatetimeLocalString = (value) => {
  const date = toDateObject(value);
  if (!date) return '';

  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const normalizeCopaSarauScheduleFields = (values = {}, options = {}) => {
  const { forForm = false } = options;
  const normalizedValues = { ...values };

  SCHEDULE_FIELDS.forEach((field) => {
    if (!(field in normalizedValues)) return;
    normalizedValues[field] = forForm
      ? toDatetimeLocalString(normalizedValues[field])
      : toIsoString(normalizedValues[field]);
  });

  return normalizedValues;
};

const normalizeSportModalities = (sports = DEFAULT_COPA_SARAU_CONFIG.modalities.sports) =>
  sports.map((sport, index) => ({
    ...DEFAULT_SPORT_MODALITY,
    ...sport,
    id: sport.id || `sport_${index + 1}`,
    name: sport.name || '',
    active: normalizeBoolean(sport.active, true),
    selection_type: sport.selection_type || 'individual',
    special_type: sport.special_type || '',
    shirt: {
      ...DEFAULT_SPORT_MODALITY.shirt,
      ...(sport.shirt || {}),
      enabled: normalizeBoolean(sport.shirt?.enabled, false),
      required: normalizeBoolean(sport.shirt?.required, false),
      sizes: normalizeList(sport.shirt?.sizes).join(', '),
    },
    categories: {
      ...DEFAULT_SPORT_MODALITY.categories,
      ...(sport.categories || {}),
      enabled: normalizeBoolean(sport.categories?.enabled, false),
      required: normalizeBoolean(sport.categories?.required, false),
      multiple: normalizeBoolean(sport.categories?.multiple, false),
      max_selection: normalizeInteger(sport.categories?.max_selection, 2),
      options: normalizeList(sport.categories?.options).join(', '),
    },
    partner_name: {
      ...DEFAULT_SPORT_MODALITY.partner_name,
      ...(sport.partner_name || {}),
      enabled: normalizeBoolean(sport.partner_name?.enabled, false),
      required: normalizeBoolean(sport.partner_name?.required, false),
      label: sport.partner_name?.label || DEFAULT_SPORT_MODALITY.partner_name.label,
    },
  }));

const normalizeSarauModalities = (sarau = DEFAULT_COPA_SARAU_CONFIG.modalities.sarau) =>
  sarau.map((item, index) => ({
    ...DEFAULT_SARAU_MODALITY,
    ...item,
    id: item.id || `sarau_${index + 1}`,
    name: item.name || '',
    active: normalizeBoolean(item.active, true),
    special_type: item.special_type || '',
    extra_text: {
      ...DEFAULT_SARAU_MODALITY.extra_text,
      ...(item.extra_text || {}),
      enabled: normalizeBoolean(item.extra_text?.enabled, false),
      required: normalizeBoolean(item.extra_text?.required, false),
      label: item.extra_text?.label || DEFAULT_SARAU_MODALITY.extra_text.label,
    },
  }));

const serializeSportModalities = (sports = []) =>
  sports.map((sport, index) => {
    const categoryOptions = normalizeList(sport.categories?.options);
    const shirtSizes = normalizeList(sport.shirt?.sizes);
    return {
      ...DEFAULT_SPORT_MODALITY,
      ...sport,
      id: sport.id || `sport_${index + 1}`,
      name: sport.name || '',
      active: normalizeBoolean(sport.active, true),
      selection_type: sport.selection_type || 'individual',
      special_type: sport.special_type || '',
      shirt: {
        ...DEFAULT_SPORT_MODALITY.shirt,
        ...(sport.shirt || {}),
        // Se o gestor preencheu tamanhos, trata como habilitado — evita
        // perder a configuração por causa do switch.
        enabled: normalizeBoolean(sport.shirt?.enabled, false) || shirtSizes.length > 0,
        required: normalizeBoolean(sport.shirt?.required, false),
        sizes: shirtSizes,
      },
      categories: {
        ...DEFAULT_SPORT_MODALITY.categories,
        ...(sport.categories || {}),
        // Mesmo raciocínio: se há opções listadas, a modalidade passa a ter
        // categorias mesmo que o switch esteja OFF.
        enabled: normalizeBoolean(sport.categories?.enabled, false) || categoryOptions.length > 0,
        required: normalizeBoolean(sport.categories?.required, false),
        multiple: normalizeBoolean(sport.categories?.multiple, false),
        max_selection: normalizeInteger(sport.categories?.max_selection, 2),
        options: categoryOptions,
      },
      partner_name: {
        ...DEFAULT_SPORT_MODALITY.partner_name,
        ...(sport.partner_name || {}),
        enabled: normalizeBoolean(sport.partner_name?.enabled, false),
        required: normalizeBoolean(sport.partner_name?.required, false),
        label: sport.partner_name?.label || DEFAULT_SPORT_MODALITY.partner_name.label,
      },
    };
  });

const serializeSarauModalities = (sarau = []) =>
  sarau.map((item, index) => ({
    ...DEFAULT_SARAU_MODALITY,
    ...item,
    id: item.id || `sarau_${index + 1}`,
    name: item.name || '',
    active: normalizeBoolean(item.active, true),
    special_type: item.special_type || '',
    extra_text: {
      ...DEFAULT_SARAU_MODALITY.extra_text,
      ...(item.extra_text || {}),
      enabled: normalizeBoolean(item.extra_text?.enabled, false),
      required: normalizeBoolean(item.extra_text?.required, false),
      label: item.extra_text?.label || DEFAULT_SARAU_MODALITY.extra_text.label,
    },
  }));

const deduplicateById = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const slugify = (value, fallback) => {
  const normalized = `${value || ''}`
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
};

const normalizePricingTiers = (tiers, legacyRoomPrice, legacyCompanionPrice) => {
  if (Array.isArray(tiers) && tiers.length > 0) {
    return tiers.map((t) => ({
      room_price: normalizeNumber(t.room_price, 0),
      companion_price: normalizeNumber(t.companion_price, 0),
    }));
  }
  return [
    {
      room_price: normalizeNumber(
        legacyRoomPrice,
        DEFAULT_HOTEL_ROOM_RULES.additional_room.pricing_tiers[0].room_price
      ),
      companion_price: normalizeNumber(legacyCompanionPrice, 0),
    },
  ];
};

const normalizeHotelRoomRules = (roomRules = {}) => ({
  room_people_limit: normalizeInteger(
    roomRules.room_people_limit,
    DEFAULT_HOTEL_ROOM_RULES.room_people_limit
  ),
  minor_age_limit: normalizeInteger(
    roomRules.minor_age_limit,
    DEFAULT_HOTEL_ROOM_RULES.minor_age_limit
  ),
  age_validation_mode:
    roomRules.age_validation_mode || DEFAULT_HOTEL_ROOM_RULES.age_validation_mode,
  first_room: {
    paying_companion_limit: normalizeInteger(
      roomRules.first_room?.paying_companion_limit,
      DEFAULT_HOTEL_ROOM_RULES.first_room.paying_companion_limit
    ),
    companion_price: normalizeNumber(
      roomRules.first_room?.companion_price,
      DEFAULT_HOTEL_ROOM_RULES.first_room.companion_price
    ),
    free_companion_limit: normalizeInteger(
      roomRules.first_room?.free_companion_limit,
      DEFAULT_HOTEL_ROOM_RULES.first_room.free_companion_limit
    ),
  },
  additional_room: {
    paying_companion_limit: normalizeInteger(
      roomRules.additional_room?.paying_companion_limit,
      DEFAULT_HOTEL_ROOM_RULES.additional_room.paying_companion_limit
    ),
    free_companion_limit: normalizeInteger(
      roomRules.additional_room?.free_companion_limit,
      DEFAULT_HOTEL_ROOM_RULES.additional_room.free_companion_limit
    ),
    charge_enabled: normalizeBoolean(
      roomRules.additional_room?.charge_enabled,
      DEFAULT_HOTEL_ROOM_RULES.additional_room.charge_enabled
    ),
    pricing_tiers: normalizePricingTiers(
      roomRules.additional_room?.pricing_tiers,
      roomRules.additional_room?.room_price,
      roomRules.additional_room?.companion_price
    ),
  },
  minors_free: normalizeBoolean(roomRules.minors_free, DEFAULT_HOTEL_ROOM_RULES.minors_free),
  minors_count_towards_room_limit: normalizeBoolean(
    roomRules.minors_count_towards_room_limit,
    DEFAULT_HOTEL_ROOM_RULES.minors_count_towards_room_limit
  ),
  minors_count_towards_payment: normalizeBoolean(
    roomRules.minors_count_towards_payment,
    DEFAULT_HOTEL_ROOM_RULES.minors_count_towards_payment
  ),
  total_rooms_available: parseInt(roomRules.total_rooms_available, 10) || 0,
  max_rooms_per_registration: parseInt(roomRules.max_rooms_per_registration, 10) || 0,
});

const buildHotelFromLegacyRoomRules = (roomRules = {}) => ({
  id: 'hotel_principal',
  name: 'Hotel principal',
  description: '',
  active: true,
  room_rules: normalizeHotelRoomRules(roomRules),
});

const normalizeHotels = (rawHotels, fallbackRoomRules) => {
  if (!Array.isArray(rawHotels) || rawHotels.length === 0) {
    return [buildHotelFromLegacyRoomRules(fallbackRoomRules)];
  }

  const usedIds = new Set();
  return rawHotels.map((hotel, index) => {
    const fallbackId = `hotel_${index + 1}`;
    let id = `${hotel.id || ''}`.trim() || slugify(hotel.name, fallbackId);
    if (!id || usedIds.has(id)) id = `${id || fallbackId}_${index + 1}`;
    usedIds.add(id);
    return {
      id,
      name: hotel.name || `Hotel ${index + 1}`,
      description: hotel.description || '',
      active: normalizeBoolean(hotel.active, true),
      room_rules: normalizeHotelRoomRules(hotel.room_rules),
    };
  });
};

const serializeHotels = (hotels = []) =>
  hotels.map((hotel, index) => ({
    id: hotel.id || `hotel_${index + 1}`,
    name: hotel.name || `Hotel ${index + 1}`,
    description: hotel.description || '',
    active: normalizeBoolean(hotel.active, true),
    room_rules: normalizeHotelRoomRules(hotel.room_rules),
  }));

export const parseCopaSarauStationConfig = (rawConfig) => {
  let parsedConfig = {};

  if (rawConfig) {
    try {
      parsedConfig = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
    } catch (e) {
      parsedConfig = {};
    }
  }

  const schemaConfig = parsedConfig.copa_sarau_config || parsedConfig || {};

  const normalizedHotels = normalizeHotels(
    schemaConfig.hotels,
    schemaConfig.room_rules || DEFAULT_HOTEL_ROOM_RULES
  );
  const primaryHotelRules = normalizedHotels[0]?.room_rules || DEFAULT_HOTEL_ROOM_RULES;

  const mergedConfig = {
    ...DEFAULT_COPA_SARAU_CONFIG,
    ...schemaConfig,
    hotels: normalizedHotels,
    modalities: {
      sports: deduplicateById(
        normalizeSportModalities(
          (schemaConfig.modalities?.sports?.length ? schemaConfig.modalities.sports : null) ||
            (parsedConfig.modalities?.sports?.length ? parsedConfig.modalities.sports : null) ||
            DEFAULT_COPA_SARAU_CONFIG.modalities.sports
        )
      ),
      sarau: deduplicateById(
        normalizeSarauModalities(
          (schemaConfig.modalities?.sarau?.length ? schemaConfig.modalities.sarau : null) ||
            (parsedConfig.modalities?.sarau?.length ? parsedConfig.modalities.sarau : null) ||
            DEFAULT_COPA_SARAU_CONFIG.modalities.sarau
        )
      ),
    },
    pending_reservation_ttl_hours:
      parseInt(
        schemaConfig.pending_reservation_ttl_hours ??
          schemaConfig.room_rules?.pending_reservation_ttl_hours,
        10
      ) || 24,
    room_rules: {
      ...primaryHotelRules,
      pending_reservation_ttl_hours:
        parseInt(
          schemaConfig.pending_reservation_ttl_hours ??
            schemaConfig.room_rules?.pending_reservation_ttl_hours,
          10
        ) || 24,
    },
    terms: {
      ...DEFAULT_COPA_SARAU_CONFIG.terms,
      ...(schemaConfig.terms || {}),
    },
  };

  const bodyMaxModalidades = normalizeInteger(
    parsedConfig.body_max_modalidades ?? mergedConfig.sports_limit,
    DEFAULT_COPA_SARAU_CONFIG.sports_limit
  );
  const bodyMaxSarau = normalizeInteger(
    parsedConfig.body_max_sarau ?? mergedConfig.sarau_limit,
    DEFAULT_COPA_SARAU_CONFIG.sarau_limit
  );
  const bodyMaxPerRoom = normalizeInteger(
    parsedConfig.body_max_per_room ?? mergedConfig.room_rules.room_people_limit,
    DEFAULT_COPA_SARAU_CONFIG.room_rules.room_people_limit
  );
  const bodyAgeLimit = normalizeInteger(
    parsedConfig.body_age_limit ?? mergedConfig.room_rules.minor_age_limit,
    DEFAULT_COPA_SARAU_CONFIG.room_rules.minor_age_limit
  );
  const bodyValorAcompanhante = normalizeNumber(
    parsedConfig.body_valor_acompanhante ?? mergedConfig.room_rules.first_room.companion_price,
    DEFAULT_COPA_SARAU_CONFIG.room_rules.first_room.companion_price
  );
  const bodyValorQuartoExtra = normalizeNumber(
    parsedConfig.body_valor_quarto_extra ??
      mergedConfig.room_rules.additional_room?.pricing_tiers?.[0]?.room_price ??
      mergedConfig.room_rules.additional_room?.room_price,
    DEFAULT_COPA_SARAU_CONFIG.room_rules.additional_room.pricing_tiers[0].room_price
  );
  const bodyTelefoneCancelamento =
    parsedConfig.body_telefone_cancelamento ?? mergedConfig.terms.cancellation_phone ?? '';

  return {
    body_max_modalidades: bodyMaxModalidades,
    body_max_sarau: bodyMaxSarau,
    body_max_per_room: bodyMaxPerRoom,
    body_age_limit: bodyAgeLimit,
    body_valor_acompanhante: bodyValorAcompanhante,
    body_valor_quarto_extra: bodyValorQuartoExtra,
    body_telefone_cancelamento: bodyTelefoneCancelamento,
    copa_sarau_config: {
      ...mergedConfig,
      sports_limit: bodyMaxModalidades,
      sarau_limit: bodyMaxSarau,
      hotels: mergedConfig.hotels,
      room_rules: {
        ...mergedConfig.room_rules,
        room_people_limit: bodyMaxPerRoom,
        minor_age_limit: bodyAgeLimit,
        first_room: {
          ...mergedConfig.room_rules.first_room,
          companion_price: bodyValorAcompanhante,
        },
        additional_room: {
          ...mergedConfig.room_rules.additional_room,
          pricing_tiers: normalizePricingTiers(
            mergedConfig.room_rules.additional_room?.pricing_tiers,
            bodyValorQuartoExtra,
            0
          ),
        },
      },
      terms: {
        ...mergedConfig.terms,
        cancellation_phone: bodyTelefoneCancelamento,
      },
    },
  };
};

export const buildCopaSarauStationConfig = (values = {}) => {
  const currentConfig = parseCopaSarauStationConfig(
    values.station_specific_data || values.copa_sarau_config || values
  );

  const normalizedValuesConfig = values.copa_sarau_config || {};

  const parsedFormConfig = parseCopaSarauStationConfig({
    ...currentConfig,
    body_max_modalidades: values.body_max_modalidades ?? currentConfig.body_max_modalidades,
    body_max_sarau: values.body_max_sarau ?? currentConfig.body_max_sarau,
    body_max_per_room: values.body_max_per_room ?? currentConfig.body_max_per_room,
    body_age_limit: values.body_age_limit ?? currentConfig.body_age_limit,
    body_valor_acompanhante:
      values.body_valor_acompanhante ?? currentConfig.body_valor_acompanhante,
    body_valor_quarto_extra:
      values.body_valor_quarto_extra ?? currentConfig.body_valor_quarto_extra,
    body_telefone_cancelamento:
      values.body_telefone_cancelamento ?? currentConfig.body_telefone_cancelamento,
    copa_sarau_config: {
      ...currentConfig.copa_sarau_config,
      ...normalizedValuesConfig,
      modalities: {
        sports:
          normalizedValuesConfig.modalities?.sports ||
          currentConfig.copa_sarau_config.modalities.sports,
        sarau:
          normalizedValuesConfig.modalities?.sarau ||
          currentConfig.copa_sarau_config.modalities.sarau,
      },
      hotels: normalizedValuesConfig.hotels?.length
        ? normalizedValuesConfig.hotels
        : currentConfig.copa_sarau_config.hotels,
      pending_reservation_ttl_hours:
        normalizedValuesConfig.pending_reservation_ttl_hours ??
        currentConfig.copa_sarau_config.pending_reservation_ttl_hours,
      room_rules: {
        ...currentConfig.copa_sarau_config.room_rules,
        ...(normalizedValuesConfig.room_rules || {}),
        first_room: {
          ...currentConfig.copa_sarau_config.room_rules.first_room,
          ...(normalizedValuesConfig.room_rules?.first_room || {}),
        },
        additional_room: {
          ...currentConfig.copa_sarau_config.room_rules.additional_room,
          ...(normalizedValuesConfig.room_rules?.additional_room || {}),
        },
      },
      terms: {
        ...currentConfig.copa_sarau_config.terms,
        ...(normalizedValuesConfig.terms || {}),
      },
    },
  });

  const serializedHotels = serializeHotels(parsedFormConfig.copa_sarau_config.hotels);
  const primaryHotel = serializedHotels[0]?.room_rules || DEFAULT_HOTEL_ROOM_RULES;
  const ttlHours =
    parseInt(parsedFormConfig.copa_sarau_config.pending_reservation_ttl_hours, 10) || 24;

  return {
    ...parsedFormConfig,
    body_max_per_room: primaryHotel.room_people_limit,
    body_age_limit: primaryHotel.minor_age_limit,
    body_valor_acompanhante: primaryHotel.first_room.companion_price,
    body_valor_quarto_extra: primaryHotel.additional_room.pricing_tiers?.[0]?.room_price ?? 0,
    copa_sarau_config: {
      ...parsedFormConfig.copa_sarau_config,
      modalities: {
        sports: serializeSportModalities(parsedFormConfig.copa_sarau_config.modalities.sports),
        sarau: serializeSarauModalities(parsedFormConfig.copa_sarau_config.modalities.sarau),
      },
      hotels: serializedHotels,
      pending_reservation_ttl_hours: ttlHours,
      // Espelho legacy: o primeiro hotel ativo "alimenta" o room_rules antigo
      // para que código que ainda lê o caminho plano continue funcionando.
      room_rules: {
        ...primaryHotel,
        pending_reservation_ttl_hours: ttlHours,
      },
    },
  };
};
